// Swagger: http://localhost:4000/contests-service/api#/contests/ContestsApiController_getContestById
// Tech Spec: https://betterpool.atlassian.net/wiki/spaces/EDGE/pages/78905353/API+Requirements+-+WIP#Get-Contests-Details

import type { ContestCreateResponse, ContestType } from '@betterpool/api-types/contests-service';
import queryString from 'query-string';
import { AxiosError } from 'axios';

import type { CreateContestPayload } from '~/domains/commissioner/interfaces/contest.interface';
import type { SortDirection } from '~/domains/common/enums/sort-direction.enum';
import { createQueryString } from '~/domains/common/utils/queryString';
import {
  contestImageUploadService,
  contestsServiceInstance,
} from '~/domains/contest/domains/services/contest.service.instance';
import { ContestStatus } from '~/domains/contest/domains/services/enums/contests-status.enum';
import type { UserContestAssociation } from '~/domains/contest/domains/services/enums/user-contest-association.enum';
import type { Contest } from '~/domains/contest/domains/services/interfaces/contest.interface';
import type { FullContest } from '~/domains/contest/domains/services/interfaces/full-contest.interface';
import type { PusherHeaders } from '~/domains/pusher/types/pusher.types';
import type { BRANDS } from '~/domains/auth/consts';

import type { PaginatedData } from '../common/interfaces/paginated-data.interface';
import type { SlateGroupPreset } from '../games-service/interfaces/slates.interface';

import type { AssetType } from './enums/contests-assets.enums';
import type { SortOptions } from './enums/contest-sort-options.enum';
import type { ContestFilters } from './interfaces/contest-filters.interface';
import type { LocationSettings } from './interfaces/contest-location-settings.interface';
import type { ContestTemplate } from './interfaces/contest-templates.interface';
import type { Entrant } from './interfaces/entrant.interface';
import type {
  AutoPickStrategyResponse,
  UpdateAutoPickStrategyRequest,
} from './interfaces/contest-auto-picks';
import type { ContestFieldEditabilityResponse } from './interfaces/contest-field-editability.interface';

export type GetContestsPayload = {
  limit: number;
  offset: number;
  filter?: {
    leagues?: string[];
    contestIds?: string[];
    entryTypes?: string[];
    contestTypes?: string[];
    payoutFormats?: string[];
    leagueId?: string;
    entryFee?: {
      min: number;
      max: number;
    };
    followedChannels?: boolean;
  };
  sort?: string;
  dir?: string;
  includeFull?: boolean;
  includeEntrantsCount?: boolean;
};

export type GetCarouselContestsPayload = {
  contestIds: string[];
  includeFull: boolean;
  includePrivate: boolean;
  includeInProgress: boolean;
};

export const downloadContestEntrants = async (contestId: string) => {
  const response = await contestsServiceInstance.get<Blob>(
    `/contest-users/contests/${contestId}/csv`,
    {
      responseType: 'blob',
      headers: {
        Accept: 'text/csv',
        'Content-Type': 'text/csv',
      },
    }
  );
  return response.data;
};

export const getContests = async (
  payload: GetContestsPayload,
  pusherHeaders?: PusherHeaders
): Promise<PaginatedData<Contest>> => {
  const response = await contestsServiceInstance.post<PaginatedData<Contest>>(
    `/contests/search`,
    payload,
    pusherHeaders ? { headers: { ...pusherHeaders } } : undefined
  );
  return response.data;
};

export const getCarouselContests = async (
  payload: GetCarouselContestsPayload,
  signal?: AbortSignal
): Promise<Contest[]> => {
  const response = await contestsServiceInstance.post<Contest[]>('/contests/carousel', payload, {
    signal,
  });
  return response.data;
};

export const getContest = async (contestId: string, authToken?: string): Promise<FullContest> => {
  const response = await contestsServiceInstance.get<FullContest>(`/contests/${contestId}`, {
    headers: authToken ? { Authorization: `Bearer ${authToken}` } : undefined,
  });
  return response.data;
};

export const getContestEntriesForInvites = async (
  contestId: string,
  limit = 100,
  offset = 0
): Promise<PaginatedData<Entrant>> => {
  const response = await contestsServiceInstance.get<PaginatedData<Entrant>>(
    `/contests/${contestId}/entries?limit=${limit}&offset=${offset}`
  );
  return response.data;
};

type GetContestsByCommissionerIdPayload = {
  association?: UserContestAssociation;
  commissionerId: string;
  contestStatuses?: ContestStatus[];
  contestType?: ContestType;
  dir?: SortDirection;
  excludeContestId?: string;
  includeEntrantsCount?: boolean;
  includePicksReminder?: boolean;
  includeUnlisted?: boolean;
  leagueId?: string;
  leagues?: string[];
  limit?: number;
  minEntries?: number;
  offset?: number;
  needsReset?: boolean;
  resetDismissed?: boolean;
  searchTerm?: string;
  sort?: SortOptions;
  source?: (typeof BRANDS)[keyof typeof BRANDS];
};
export const getContestsByCommissionerId = async ({
  association,
  commissionerId,
  contestStatuses = [ContestStatus.SCHEDULED],
  contestType,
  dir,
  excludeContestId,
  includeEntrantsCount = true,
  includePicksReminder = false,
  includeUnlisted = true,
  leagueId,
  leagues,
  limit = 50,
  minEntries = 0,
  needsReset,
  offset = 0,
  resetDismissed,
  searchTerm,
  sort,
  source,
}: GetContestsByCommissionerIdPayload): Promise<PaginatedData<Contest>> => {
  const query = queryString.stringify({
    limit,
    offset,
    sort,
    dir,
    minEntries,
    contestStatuses,
    includePicksReminder,
    contestType,
    leagueId,
    leagues,
    association,
    includeEntrantsCount,
    includeUnlisted,
    needsReset,
    resetDismissed,
    source,
    ...(searchTerm?.length && { searchQuery: searchTerm }),
    excludeContestId,
  });
  const response = await contestsServiceInstance.get(
    `/commissioners/${commissionerId}/contests?${query}`
  );

  return response.data;
};

export type CommishContestSummary = {
  status: ContestStatus;
  contestsCount: number;
  entriesCount: number;
  fillRate: number;
};
export const getContestSummariesByCommissionerId = async (
  commissionerId: string
): Promise<CommishContestSummary[]> => {
  const response = await contestsServiceInstance.get(
    `/commissioners/${commissionerId}/contests/summaries`
  );

  return response.data;
};

type CommissionerContestsEntriesSummary = {
  firstTimeEntrantsCount: number;
  usersMissingPicksCount: number;
  entryFeesTotal: number;
  entriesCount: number;
  maxEntriesCount: number;
};
export const getContestEntriesSummaryByCommissionerId = async (
  commissionerId: string,
  contestId: string
): Promise<CommissionerContestsEntriesSummary> => {
  const response = await contestsServiceInstance.get(
    `/commissioners/${commissionerId}/contests/${contestId}/entries/summary`
  );

  return response.data;
};

export const getContestTemplatesByLeagueId = async (
  leagueId: string,
  authToken?: string
): Promise<ContestTemplate[]> => {
  const response = await contestsServiceInstance.get<ContestTemplate[]>(
    `/leagues/${leagueId}/contest-templates`,
    {
      headers: authToken ? { Authorization: `Bearer ${authToken}` } : undefined,
    }
  );
  return response.data;
};

export const getContestTemplateById = async (
  contestTemplateId: string,
  authToken?: string
): Promise<ContestTemplate> => {
  const response = await contestsServiceInstance.get<ContestTemplate>(
    `/contest-templates/${contestTemplateId}`,
    {
      headers: authToken ? { Authorization: `Bearer ${authToken}` } : undefined,
    }
  );
  return response.data;
};

export const getContestTemplatesSlateGroupsPresets = async (
  leagueId: string,
  contestTemplateId: string,
  authToken?: string
): Promise<SlateGroupPreset[]> => {
  // Cache policy is set to 10sec on API level
  try {
    const response = await contestsServiceInstance.get<SlateGroupPreset[]>(
      `/leagues/${leagueId}/contest-templates/${contestTemplateId}/slate-groups-presets`,
      {
        headers: authToken ? { Authorization: `Bearer ${authToken}` } : undefined,
      }
    );
    return response.data;
  } catch (error) {
    if (error instanceof AxiosError && error.response?.status === 404) {
      return [];
    }
    throw error;
  }
};

export const getPopularContestForCreation = async (
  authToken?: string
): Promise<{ title: string; contestId: string; subtitle?: string }[]> => {
  // Cache policy is set to 10sec on API level
  try {
    const response = await contestsServiceInstance.get<
      {
        title: string;
        subtitle: string;
        contestId: string;
      }[]
    >(`/contests/create/popular`, {
      headers: authToken ? { Authorization: `Bearer ${authToken}` } : undefined,
    });

    return response.data;
  } catch (error) {
    if (error instanceof AxiosError && error.response?.status === 404) {
      return [];
    }
    throw error;
  }
};

export const dismissContestRestart = async (contestId: string) => {
  const response = await contestsServiceInstance.patch(`/contest-metadata/${contestId}`, {
    resetDismissed: true,
  });
  return response.data;
};

export const postCreateContest = async (payload: CreateContestPayload, authToken?: string) => {
  const response = await contestsServiceInstance.post<ContestCreateResponse>('/contests', payload, {
    headers: authToken ? { Authorization: `Bearer ${authToken}` } : undefined,
  });
  return response.data;
};

export const cancelContest = async (contestId: string) => {
  const response = await contestsServiceInstance.patch(`/contests/${contestId}`, {
    status: ContestStatus.CANCELLED,
  });
  return response.data;
};

export const updateContest = async (
  contestId: string,
  payload: Partial<CreateContestPayload>,
  authToken?: string
) => {
  const response = await contestsServiceInstance.patch(`/contests/${contestId}`, payload, {
    headers: authToken ? { Authorization: `Bearer ${authToken}` } : undefined,
  });
  return response.data;
};

export const uploadContestAsset = async (payload: {
  file: File;
  assetType: AssetType;
}): Promise<{ id: string; url: string }> => {
  const response = await contestImageUploadService.post('/', payload);
  return response.data;
};

export const getContestFiltersByLeagueId = async (
  leagueId: string,
  authToken?: string
): Promise<ContestFilters> => {
  const response = await contestsServiceInstance.get<ContestFilters>(
    `/contests/filters${createQueryString({ leagueId })}`,
    {
      headers: authToken ? { Authorization: `Bearer ${authToken}` } : undefined,
    }
  );
  return response.data;
};

export const getLocationSettings = async (authToken?: string): Promise<LocationSettings> => {
  const response = await contestsServiceInstance.get<LocationSettings>(`/tags/location-settings`, {
    headers: authToken ? { Authorization: `Bearer ${authToken}` } : undefined,
  });
  return response.data;
};

// ------------------------------ MANAGE ENTRANTS ------------------------------
export type FullEntrant = {
  id: string;
  user: {
    avatar: string;
    email: string;
    firstName: string;
    lastName: string;
    handle: string;
    id: string;
  };
  notes?: string;
  disabled?: boolean;
  booted?: boolean;
  entryCount?: number;
  entriesWithPicks?: number;
  needsPicks?: number; // @deprecated - use needsPicksCount instead
  needsPicksCount?: number; // New field from API
  survivingEntriesCount?: number;
  survivingEntriesWithPicksCount?: number;
  enteredAt?: Date;
};
type GetEntrantsForContestResponse = PaginatedData<FullEntrant>;

export const getEntrantsForContest = async (
  contestId: string,
  offset = 0,
  limit = 1,
  search?: string,
  filters?: {
    survivingEntries?: boolean;
    missingPicks?: boolean;
    booted?: boolean;
    disabled?: boolean;
    hasNotes?: boolean;
  },
  sort?: string,
  sortDirection?: 'ASC' | 'DESC'
): Promise<GetEntrantsForContestResponse> => {
  const queryParams = {
    offset,
    limit,
    search,
    sort,
    sortDirection,
    ...filters,
  };

  const response = await contestsServiceInstance.get(
    `/contest-users/contests/${contestId}${createQueryString(queryParams)}`
  );
  return response.data;
};

export type ContestUserStats = {
  avgEntryCount: number;
  disabledCount: number;
  entrantsCount: number;
  activeEntrantsCount: number;
  entriesCount: number;
  survivorsCount: number;
  missingPicksCount: number;
  removedCount: number;
  eliminatedEntries?: number;
  survivingEntries?: number;
  notesCount: number;
  noNotesCount: number;
  totalEntries: number;
};

export const getContestUserStats = async (contestId: string): Promise<ContestUserStats> => {
  const response = await contestsServiceInstance.get<ContestUserStats>(
    `/contest-users/contests/${contestId}/stats`
  );
  return response.data;
};

export const updateEntrantNotes = async (contestId: string, entrantId: string, notes: string) => {
  const response = await contestsServiceInstance.patch(
    `/contest-users/${entrantId}/contests/${contestId}`,
    { notes }
  );
  return response.data;
};

export const updateContestEntrantStatus = async (
  contestId: string,
  entrantId: string,
  disabled: boolean
) => {
  const response = await contestsServiceInstance.patch(
    `/contest-users/${entrantId}/contests/${contestId}`,
    { disabled }
  );
  return response.data;
};

// We don't support unbooting entrants, when we do we can add a boolean to the payload or a separate function
export const bootContestEntrant = async (contestId: string, entrantId: string) => {
  const response = await contestsServiceInstance.patch(
    `/contest-users/${entrantId}/contests/${contestId}`,
    { booted: true }
  );
  return response.data;
};

// Bulk operations for contest entrants
export type BulkContestEntrantUpdate = {
  ids: string[];
  notes?: string;
  disabled?: boolean;
  booted?: boolean;
};

export const bulkUpdateContestEntrants = async (
  contestId: string,
  payload: BulkContestEntrantUpdate
) => {
  const response = await contestsServiceInstance.patch(
    `/contest-users/contests/${contestId}/bulk`,
    payload
  );
  return response.data;
};

export const renameEntry = async (entryId: string, payload: { name: string }) => {
  const response = await contestsServiceInstance.put(`/entries/${entryId}`, payload);
  return response.data;
};

export const removeSingleEntry = async (entryId: string) => {
  const response = await contestsServiceInstance.delete(`/entries/${entryId}`);
  return response.data;
};

export const getAutoPickStrategy = async (contestId: string): Promise<AutoPickStrategyResponse> => {
  const response = await contestsServiceInstance.get<AutoPickStrategyResponse>(
    `/contest-users/contests/${contestId}/auto-pick-strategy`
  );
  return response.data;
};

export const updateAutoPickStrategy = async (
  contestId: string,
  data: UpdateAutoPickStrategyRequest
): Promise<void> => {
  await contestsServiceInstance.patch(
    `/contest-users/contests/${contestId}/auto-pick-strategy`,
    data
  );
};

export const getContestFieldEditability = async (
  contestId: string,
  authToken?: string
): Promise<ContestFieldEditabilityResponse> => {
  const response = await contestsServiceInstance.get<ContestFieldEditabilityResponse>(
    `/contests/${contestId}/field-editability`,
    authToken ? { headers: { Authorization: `Bearer ${authToken}` } } : undefined
  );
  return response.data;
};

// ------------------------------ CONTEST MODIFICATIONS ------------------------------
export const ContestModificationType = {
  PICKS_CHANGE: 'picks_change',
  ENTRY_PAUSE: 'entry_pause',
  ENTRY_RESUME: 'entry_resume',
  ENTRANT_REMOVAL: 'entrant_removal',
  ENTRY_DELETION: 'entry_deletion',
} as const;

export type ContestModificationTypeValue =
  (typeof ContestModificationType)[keyof typeof ContestModificationType];

export type ContestModification = {
  id: string;
  type: ContestModificationTypeValue;
  createdAt: string; // ISO 8601 date string
  userId: string;
  entrant: string; // User handle/username
  entrantFirstName: string;
  entrantLastName: string;
  entryId?: string;
  entryName?: string;
  modification: string; // Description of the modification
  removedEntries?: string[] | null; // Additional modification-specific data
};

export type ContestModificationsResponse = PaginatedData<ContestModification>;

export const getContestModifications = async (
  contestId: string,
  offset = 0,
  limit = 50,
  search?: string,
  sort?: string,
  sortDirection?: 'ASC' | 'DESC'
): Promise<ContestModificationsResponse> => {
  const queryParams = {
    offset,
    limit,
    ...(search && { search }),
    ...(sort && { sort }),
    ...(sortDirection && { sortDirection }),
  };

  const response = await contestsServiceInstance.get<ContestModificationsResponse>(
    `/contests/${contestId}/modifications${createQueryString(queryParams)}`
  );
  return response.data;
};
